import React from 'react';

import OAuthRedirect from '../components/OAuthRedirect';
import { LoginComponentProps } from '../types/LoginFormData';
import { OAuthHandlers, TSPCategory, TSPConfig } from './types';

// Collection of all TSP configurations (will be populated by the index.tsx)
let tspConfigurations: TSPConfig[] = [];

// Set the TSP configurations (called from index.tsx)
export function setTSPConfigurations(configs: TSPConfig[]): void {
  tspConfigurations = configs;
}

// Helper functions for accessing TSP data
export function getTSPByKey(key: string): TSPConfig | undefined {
  return tspConfigurations.find((tsp) => tsp.key === key);
}

export function getTSPNameByKey(key: string): string {
  const tsp = getTSPByKey(key);
  return tsp ? tsp.name : '';
}

export function getTSPImageNameByKey(key: string): string {
  const tsp = getTSPByKey(key);
  return tsp ? tsp.imageName || tsp.name : '';
}

export function getTSPHelpTextByKey(key: string): React.ReactNode | null {
  const tsp = getTSPByKey(key);
  return tsp ? tsp.helpText : null;
}

// Get all OAuth TSPs
export function getOAuthTSPs() {
  return tspConfigurations.filter((tsp) => tsp.category === TSPCategory.OAUTH);
}

// Create redirect component for OAuth TSP
export function createRedirectComponent(tspKey: string) {
  // Dynamic component that renders the OAuthRedirect with the proper TSP
  return () => <OAuthRedirect TSP={getTSPNameByKey(tspKey)} />;
}

// Generate routes for OAuth redirects
export function generateOAuthRedirectRoutes() {
  return getOAuthTSPs().map((tsp) => ({
    path: `/${tsp.key.toLowerCase()}`,
    element: <OAuthRedirect TSP={tsp.name} />,
    key: `${tsp.key}-redirect`,
  }));
}

// Create a mapping function for components (replacement for TSPLoginMappings.tsx)
export function createTSPLoginMapping(
  oAuthHandlers: OAuthHandlers,
  props: LoginComponentProps
): Record<string, () => JSX.Element> {
  const mapping: Record<string, () => JSX.Element> = {};

  tspConfigurations.forEach((tsp) => {
    mapping[tsp.key] = () => {
      const component = tsp.loginComponent(props, oAuthHandlers);
      return component;
    };
  });

  return mapping;
}

// Export an array format for backwards compatibility
export function getTSPArray() {
  return tspConfigurations.map((tsp) => ({
    key: tsp.key,
    name: tsp.name,
    imageName: tsp.imageName || tsp.name,
    helpText: tsp.helpText,
  }));
}
